import React, { ReactElement, useState } from 'react';
import { useForm } from 'react-hook-form';
import validator from 'validator';
import { useNavigate, useLocation, To } from 'react-router-dom';

import { LocationState } from '~/models';
import VARIANTS from '~/constants/variants';
import PATHS from '~/constants/paths';
import COLORS from '~/constants/colors';
import { useAuth } from '~/providers/Auth';
import TextInput from '~/components/inputs/TextInput';
import Subtext from '~/components/titles/Subtext';
import Button from '~/components/buttons/Button';
import Gap from '~/components/flow/Gap';
import { getFormErrorHandler } from '~/helpers/journal';
import Announcement from '~/components/Announcement';

import { DEFAULT_SIGNUP } from '../constants';

function Form(): ReactElement {
  const [fail, setFail] = useState<string | undefined>();
  const navigate = useNavigate();
  const { state } = useLocation() as { state: LocationState };
  const { signup } = useAuth();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: DEFAULT_SIGNUP });
  const emailOpts = {
    required: true,
    validate: (email) => validator.isEmail(email),
  };
  const passwordOpts = { required: true, minLength: 6 };
  const handleOpts = {
    required: true,
    validate: (handle) => validator.isAlphanumeric(handle),
  };
  const signupButtonText = 'Sign Up';
  const signupButtonLoadingText = 'Registering...';
  const cancelButtonText = 'Cancel';

  // Handlers
  const onSignUp = (signupInfo) => {
    return new Promise<void>((resolve) => {
      setFail(undefined);
      signup(signupInfo)
        .then(() => navigate(state?.from || PATHS.root))
        .catch(getFormErrorHandler(DEFAULT_SIGNUP, setError, setFail, resolve));
    });
  };
  const handleCancel = () => navigate((state?.from || -1) as To);

  // Styling
  const textInputStyle = {
    width: 'min(320px, 75vw)',
  };
  const buttonHolderStyle = {
    textAlign: 'center' as const,
  };
  const signupButtonStyle = {
    width: '63%',
    marginRight: '3%',
  };
  const cancelButtonStyle = {
    width: '33%',
  };
  const announcementStyle = {
    width: 'min(calc(320px - 1.5em), calc(75vw - 1.5em))',
  };

  return (
    <>
      {fail && (
        <>
          <Announcement style={announcementStyle}>{fail}</Announcement>
          <Gap />
        </>
      )}
      <form>
        <div>
          <TextInput
            {...register('email', emailOpts)}
            type="email"
            placeholder="Email"
            style={textInputStyle}
          />
          {errors.email && (
            <Subtext color={COLORS.mandy}>
              {errors.email.message || 'Valid email required'}
            </Subtext>
          )}
        </div>
        <Gap />
        <div>
          <TextInput
            {...register('password', passwordOpts)}
            type="password"
            placeholder="Password"
            style={textInputStyle}
          />
          {errors.password && (
            <Subtext color={COLORS.mandy}>
              {errors.password.message ||
                'Password with at least 6 characters required'}
            </Subtext>
          )}
        </div>
        <Gap />
        <div>
          <TextInput
            {...register('handle', handleOpts)}
            placeholder="Handle"
            style={textInputStyle}
          />
          {errors.handle && (
            <Subtext color={COLORS.mandy}>
              {errors.handle.message ||
                'Handle with only letters and numbers required'}
            </Subtext>
          )}
        </div>
        <Gap />
        <div style={buttonHolderStyle}>
          <Button
            label={signupButtonText}
            loadingLabel={signupButtonLoadingText}
            loading={isSubmitting}
            onClick={handleSubmit(onSignUp)}
            onTouchEnd={handleSubmit(onSignUp)}
            style={signupButtonStyle}
          />
          <Button
            label={cancelButtonText}
            variant={VARIANTS.secondary}
            onClick={handleCancel}
            onTouchEnd={handleCancel}
            style={cancelButtonStyle}
          />
        </div>
      </form>
    </>
  );
}

export default Form;
